import { MeshClipStateSet } from '@/plugins/mesh/clipping/MeshClipStateSet'
import type { ITileContentAdapter } from '@/plugins/tiles/contracts'
import { createCollisionGeometry } from '@/plugins/tiles/mesh/createCollisionGeometry'
import type { GltfContentDecoderOptions } from '@/plugins/tiles/mesh/GltfContentDecoder'
import { GltfContentDecoder } from '@/plugins/tiles/mesh/GltfContentDecoder'
import { pickMeshTiles } from '@/plugins/tiles/mesh/pickMeshTiles'
import type { TilesRenderBridgeOptions } from '@/plugins/tiles/mesh/TilesRenderBridge'
import { TilesRenderBridge } from '@/plugins/tiles/mesh/TilesRenderBridge'
import { getLoggerManager } from '@/shared/logging'

const log = getLoggerManager().getLogger('tiles:mesh:quality')

export interface MeshContentAdapterOptions
  extends GltfContentDecoderOptions,
    TilesRenderBridgeOptions {}

/** 显式装配 Mesh 能力；通用 Tiles 入口不导入此工厂。 */
export function createMeshContentAdapter(
  options: MeshContentAdapterOptions = {}
): ITileContentAdapter {
  const decoder = new GltfContentDecoder(options)
  const bridge = new TilesRenderBridge(options)
  const clips = new MeshClipStateSet()
  return {
    id: 'gltf-mesh',
    getImageStatus: () => decoder.getImageStatus(),
    getTextureQualityPolicy: () => decoder.qualityPolicy,
    estimateTextureQuality: (content, size) => decoder.qualityUpdater.estimate(content, size),
    updateTextureQuality: async (content, size, context) => {
      const update = await decoder.qualityUpdater.update(content, size, context)
      return {
        ...update,
        commit: () => {
          const previous = update.commit()
          try {
            bridge.invalidateContent(content)
          } catch (error) {
            log.warn(`旧绘制代理清理失败: ${String(error)}`)
          }
          return previous
        },
      }
    },
    createCollisionGeometry,
    setAppearance: (assetId, appearance) => bridge.setAppearance(assetId, appearance),
    setClipVolumes: (volumes) => {
      clips.update(volumes)
    },
    pick: (snapshot, query) => pickMeshTiles(snapshot, query, clips.get(snapshot.viewId).state),
    estimate: (data, purpose, size) => decoder.estimate(data, purpose, size),
    decode: (data, context) => decoder.decode(data, context),
    prepare: (content, context) => bridge.prepare(content, context),
    createPreparation: (content, context, version) =>
      bridge.createPreparation(content, context, version),

    render: (items, frame, phase, context) =>
      bridge.render(items, frame, phase, context, clips.get(frame.viewId).packet),
    dispose: () => {
      try {
        bridge.dispose()
      } finally {
        try {
          clips.dispose()
        } finally {
          decoder.dispose()
        }
      }
    },
  }
}
